import {
  clientTypeOverride,
  isMultiTenant,
  useExternalCRMOverride,
  useWasteSuctionSystemOverride,
  wtcsOverride,
} from "@/Configs/config";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { useConfigService } from "@/pages/configuration/useConfigService";

import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import moment from "moment-timezone";
import ClientType from "server/utils/clientType.js";
import { create } from "zustand";
import { combine } from "zustand/middleware";

import { useSelectedSite } from "./useSelectedSite";

export const timezoneStore = create<{
  timezone: string;
  setTimezone: (timezone: string) => void;
}>(
  combine(
    {
      timezone: "",
    } as any,
    (set) =>
      ({
        setTimezone: (timezone: string) => set({ timezone }),
      }) as any,
  ),
);

export type OptionalClientType = ClientType | undefined;

export const useConfig = () => {
  const {
    data: configData,
    refetch,
    isLoading: isConfigLoading,
    isError: isErrorFetchingConfig,
  } = trpc.config.getClientConfig.useQuery(undefined, {
    retryOnMount: false,
  });
  const { setGlobalAlert } = useGlobalAlert();
  const { siteName: selectedSiteName } = useSelectedSite();
  const { client: urlClientName } = useParams();
  const siteName = urlClientName || selectedSiteName;

  const isMWM = (clientTypeOverride ?? configData?.type) === ClientType.Mwm;
  const isRealEstate =
    (clientTypeOverride ?? configData?.type) === ClientType.RealEstate;
  const useExternalCRM = useExternalCRMOverride ?? configData?.useExternalCRM;
  const { setTimezone } = timezoneStore();
  const { terminalConfig } = useConfigService();

  const {
    mutate: updateConfig,
    mutateAsync: updateConfigAsync,
    isPending: isUpdatingConfig,
  } = trpc.config.updateClientConfig.useMutation({
    onSuccess: () => {
      refetch();
      setGlobalAlert({
        type: "success",
        message: "systemMessages:changesSaved",
      });
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:failedSave",
        instructions: error.message,
      });
    },
  });

  const { mutateAsync: updateClientAsync } =
    trpc.config.updateClient.useMutation({
      onSuccess: () => {
        refetch();
        setGlobalAlert({
          type: "success",
          message: "systemMessages:changesSaved",
        });
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:failedSave",
          instructions: error.message,
        });
      },
    });

  const config = useMemo(
    () => ({
      ...configData,
      useExternalCRM,
      type: (clientTypeOverride ?? configData?.type) as OptionalClientType,
      wasteTypeClassificationSystemId:
        wtcsOverride ?? configData?.wasteTypeClassificationSystemId,
      useRedundancyPoints: isMWM && useExternalCRM,
      useWasteSuctionSystem:
        useWasteSuctionSystemOverride ?? configData?.useWasteSuctionSystem,
      timezone: configData?.timezone,
      enableTenantPayments: terminalConfig?.enableTenantPayments,
      currency: terminalConfig?.currency || configData?.currency,
    }),
    [configData, isMWM, useExternalCRM, terminalConfig],
  );

  useEffect(() => {
    if (terminalConfig || config) {
      setTimezone(
        terminalConfig?.timezone || config?.timezone || moment.tz.guess(),
      );
    }
  }, [terminalConfig, config, setTimezone]);

  // If there are any overrides present that don't match config, update the config and reload the page
  useEffect(() => {
    const shouldUpdateType =
      clientTypeOverride && clientTypeOverride !== configData?.type;
    const shouldUpdateUseExternalCRM =
      useExternalCRMOverride &&
      useExternalCRMOverride !== configData?.useExternalCRM;
    const shouldUpdateWtcs =
      wtcsOverride &&
      wtcsOverride !== configData?.wasteTypeClassificationSystemId;
    const shouldUpdateAny =
      shouldUpdateType || shouldUpdateUseExternalCRM || shouldUpdateWtcs;
    if (shouldUpdateAny && configData) {
      updateConfigAsync({
        type: (clientTypeOverride as ClientType) ?? configData.type,
        useExternalCRM: useExternalCRMOverride ?? configData.useExternalCRM,
        wasteTypeClassificationSystemId:
          wtcsOverride ?? configData.wasteTypeClassificationSystemId ?? "",
        useWasteSuctionSystem:
          useWasteSuctionSystemOverride ?? configData.useWasteSuctionSystem,
      }).then(() => {
        const shouldUpdateSiteName = !isMultiTenant && !configData?.siteName;
        if (configData && shouldUpdateSiteName) {
          updateClientAsync({
            siteName,
          }).then(() => {
            window.location.reload();
          });
        } else {
          window.location.reload();
        }
      });
    }
  }, [configData, updateConfigAsync, updateClientAsync, siteName]);

  useEffect(() => {
    const shouldUpdateSiteName = !isMultiTenant && !configData?.siteName;
    if (configData && shouldUpdateSiteName) {
      updateClientAsync({
        siteName,
      }).then(() => {
        window.location.reload();
      });
    }
  }, [configData, updateClientAsync, siteName]);

  const useClientPath = isRealEstate && isMultiTenant;

  return {
    config,
    refetch,
    updateConfig,
    isMWM,
    isRealEstate,
    useClientPath,
    isConfigLoading,
    isUpdatingConfig,
    isErrorFetchingConfig,
  };
};
